const stripCodeFences = (value = '') => {
  const trimmed = String(value || '').trim();
  const match = trimmed.match(/```(?:json)?\s*([\s\S]*?)```/i);
  return match ? match[1].trim() : trimmed;
};

const escapeNewlinesInsideStrings = (value = '') => {
  let result = '';
  let inString = false;
  let escaping = false;

  for (const char of String(value || '')) {
    if (escaping) {
      result += char;
      escaping = false;
      continue;
    }

    if (char === '\\') {
      result += char;
      escaping = true;
      continue;
    }

    if (char === '"') {
      inString = !inString;
      result += char;
      continue;
    }

    if (inString && (char === '\n' || char === '\r')) {
      result += '\\n';
      continue;
    }

    result += char;
  }

  return result;
};

const getJsonCandidates = (value = '') => {
  const stripped = stripCodeFences(value);
  const candidates = [stripped];
  const firstBrace = stripped.indexOf('{');
  const lastBrace = stripped.lastIndexOf('}');

  if (firstBrace !== -1 && lastBrace !== -1 && lastBrace > firstBrace) {
    candidates.push(stripped.slice(firstBrace, lastBrace + 1));
  }

  return [...new Set(candidates.filter(Boolean))];
};

const normalizeBulletArray = (items) => items
  .map(item => String(item || '').trim())
  .filter(Boolean)
  .map(item => (item.startsWith('- ') || item.startsWith('• ')) ? item : `- ${item}`)
  .join('\n');

const normalizeFieldValue = (value) => {
  if (Array.isArray(value)) {
    return normalizeBulletArray(value);
  }

  if (typeof value !== 'string') {
    return '';
  }

  return value
    .replace(/\\n/g, '\n')
    .replace(/\\"/g, '"')
    .trim();
};

const tryParseJsonObject = (value) => {
  const parsed = JSON.parse(value);
  if (!parsed || typeof parsed !== 'object') {
    throw new Error('Parsed value is not an object');
  }
  return {
    historicalContext: normalizeFieldValue(parsed.historicalContext),
    culturalSignificance: normalizeFieldValue(parsed.culturalSignificance)
  };
};

const extractFieldBlock = (text, fieldName, nextFieldName) => {
  const pattern = nextFieldName
    ? new RegExp(`"${fieldName}"\\s*:\\s*([\\s\\S]*?)(?=,\\s*"${nextFieldName}"\\s*:)`)
    : new RegExp(`"${fieldName}"\\s*:\\s*([\\s\\S]*?)(?=\\s*}\\s*$)`);
  const match = String(text || '').match(pattern);
  return match ? match[1].trim().replace(/,$/, '').trim() : '';
};

const extractLooseObjectField = (text, fieldName, nextFieldName) => {
  const pattern = nextFieldName
    ? new RegExp(`${fieldName}\\s*:\\s*([\\s\\S]*?)(?=,?\\s*${nextFieldName}\\s*:)`, 'i')
    : new RegExp(`${fieldName}\\s*:\\s*([\\s\\S]*?)(?=\\s*}\\s*$|$)`, 'i');
  const match = String(text || '').match(pattern);
  return match ? match[1].trim().replace(/,$/, '').trim() : '';
};

const normalizeBulletPrefix = (value) => String(value || '')
  .replace(/[•●▪◦]/g, '-')
  .replace(/\r\n/g, '\n')
  .trim();

const sanitizeSectionText = (value) => normalizeBulletPrefix(value)
  .replace(/^["']|["']$/g, '')
  .replace(/\[\[\/?(HISTORICAL_CONTEXT|CULTURAL_SIGNIFICANCE)\]\]/gi, '')
  .replace(/^\s*(historical\s*context|historicalcontext)\s*:?\s*/i, '')
  .replace(/^\s*(cultural\s*significance|culturalsignificance)\s*:?\s*/i, '')
  .trim();

const extractLabeledSection = (text, labels, nextLabels = []) => {
  const current = labels.join('|');
  const next = nextLabels.length > 0 ? `(?=\\n\\s*(?:${nextLabels.join('|')})\\s*:?)` : '(?=$)';
  const pattern = new RegExp(`(?:^|\\n)\\s*(?:${current})\\s*:?\\s*\\n?([\\s\\S]*?)${next}`, 'i');
  const match = String(text || '').match(pattern);
  return match ? sanitizeSectionText(match[1]) : '';
};

const splitPlainBulletSections = (text) => {
  const normalized = normalizeBulletPrefix(text);
  const blocks = normalized
    .split(/\n\s*\n+/)
    .map(block => block.trim())
    .filter(Boolean);

  if (blocks.length >= 2) {
    return {
      historicalContext: sanitizeSectionText(blocks[0]),
      culturalSignificance: sanitizeSectionText(blocks[1])
    };
  }

  return {
    historicalContext: '',
    culturalSignificance: ''
  };
};

const extractDelimitedSection = (text, startTag, endTag) => {
  const pattern = new RegExp(`${startTag}\\s*([\\s\\S]*?)\\s*${endTag}`, 'i');
  const match = String(text || '').match(pattern);
  if (match) {
    return sanitizeSectionText(match[1]);
  }

  const startPattern = new RegExp(`${startTag}\\s*([\\s\\S]*)`, 'i');
  const fallbackMatch = String(text || '').match(startPattern);
  return fallbackMatch ? sanitizeSectionText(fallbackMatch[1]) : '';
};

const parseLooseFieldValue = (value) => {
  const trimmed = String(value || '').trim();
  if (!trimmed) return '';

  if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
    try {
      return normalizeFieldValue(JSON.parse(escapeNewlinesInsideStrings(trimmed)));
    } catch {
      return trimmed;
    }
  }

  if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
    return trimmed
      .slice(1, -1)
      .replace(/\\n/g, '\n')
      .replace(/\\"/g, '"')
      .trim();
  }

  return trimmed
    .replace(/\\n/g, '\n')
    .replace(/\\"/g, '"')
    .trim();
};

export const parseSupplementaryContentResponse = (rawResponse) => {
  const rawText = String(rawResponse || '');
  const delimitedHistorical = extractDelimitedSection(
    rawText,
    '\\[\\[HISTORICAL_CONTEXT\\]\\]',
    '\\[\\[/HISTORICAL_CONTEXT\\]\\]'
  );
  const delimitedCultural = extractDelimitedSection(
    rawText,
    '\\[\\[CULTURAL_SIGNIFICANCE\\]\\]',
    '\\[\\[/CULTURAL_SIGNIFICANCE\\]\\]'
  );

  if (delimitedHistorical || delimitedCultural) {
    return {
      historicalContext: delimitedHistorical,
      culturalSignificance: delimitedCultural
    };
  }

  const candidates = getJsonCandidates(rawResponse);

  for (const candidate of candidates) {
    try {
      return tryParseJsonObject(candidate);
    } catch {}

    try {
      return tryParseJsonObject(escapeNewlinesInsideStrings(candidate));
    } catch {}
  }

  const source = candidates[0] || String(rawResponse || '');
  const historicalContext = parseLooseFieldValue(
    extractFieldBlock(source, 'historicalContext', 'culturalSignificance') ||
    extractLooseObjectField(source, 'historicalContext', 'culturalSignificance') ||
    extractLabeledSection(
      source,
      ['historical\\s*context', 'historicalcontext'],
      ['cultural\\s*significance', 'culturalsignificance']
    )
  );
  const culturalSignificance = parseLooseFieldValue(
    extractFieldBlock(source, 'culturalSignificance') ||
    extractLooseObjectField(source, 'culturalSignificance') ||
    extractLabeledSection(
      source,
      ['cultural\\s*significance', 'culturalsignificance']
    )
  );

  if (historicalContext || culturalSignificance) {
    return {
      historicalContext,
      culturalSignificance
    };
  }

  const plainSections = splitPlainBulletSections(source);
  if (plainSections.historicalContext || plainSections.culturalSignificance) {
    return plainSections;
  }

  throw new Error('Failed to parse supplementary content response');
};

const extractStopSection = (text, stopId) => {
  const escapedId = String(stopId).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const pattern = new RegExp(`\\[\\[STOP:${escapedId}\\]\\]([\\s\\S]*?)(?=\\[\\[STOP:|$)`, 'i');
  const match = String(text || '').match(pattern);
  return match ? match[1] : '';
};

export const parseBatchSupplementaryContentResponse = (rawResponse, stopIds = []) => {
  const rawText = String(rawResponse || '');
  const results = {};
  let populatedStops = 0;

  for (const stopId of stopIds) {
    const stopSection = extractStopSection(rawText, stopId);
    if (!stopSection) continue;

    let parsedSection = null;

    try {
      parsedSection = parseSupplementaryContentResponse(stopSection);
    } catch {
      parsedSection = {
        historicalContext: extractDelimitedSection(
          stopSection,
          '\\[\\[HISTORICAL_CONTEXT\\]\\]',
          '\\[\\[/HISTORICAL_CONTEXT\\]\\]'
        ),
        culturalSignificance: extractDelimitedSection(
          stopSection,
          '\\[\\[CULTURAL_SIGNIFICANCE\\]\\]',
          '\\[\\[/CULTURAL_SIGNIFICANCE\\]\\]'
        )
      };
    }

    const historicalContext = parsedSection.historicalContext || '';
    const culturalSignificance = parsedSection.culturalSignificance || '';

    results[String(stopId)] = {
      historicalContext,
      culturalSignificance
    };

    if (historicalContext || culturalSignificance) {
      populatedStops += 1;
    }
  }

  if (Object.keys(results).length === 0) {
    throw new Error('Failed to parse batch supplementary content response');
  }

  if (populatedStops === 0) {
    throw new Error('Batch supplementary content response did not contain any usable supplementary content');
  }

  return results;
};

export default parseSupplementaryContentResponse;
